import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';

interface ConnectWaitingPanelProps {
  openedAt: number;
  onHelpShown: () => void;
  onReopen: () => void;
  onContinueInTab: () => void;
  onCancel: () => void;
  continueLoading: boolean;
}

const WAITING_HELP_DELAY_MS = 2 * 60_000;

export const ConnectWaitingPanel = ({
  openedAt,
  onHelpShown,
  onReopen,
  onContinueInTab,
  onCancel,
  continueLoading,
}: ConnectWaitingPanelProps) => {
  const { t } = useTranslation(['integrations']);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    setShowHelp(false);
    const timeout = window.setTimeout(
      () => setShowHelp(true),
      Math.max(0, openedAt + WAITING_HELP_DELAY_MS - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [openedAt]);

  useEffect(() => {
    if (showHelp) onHelpShown();
  }, [showHelp, onHelpShown]);

  return (
    <Alert
      variant="info"
      role="status"
      aria-live="polite"
      data-testid="whatsapp-connect-waiting-panel"
    >
      <Spinner className="size-4" />
      <AlertTitle>
        {t(
          showHelp
            ? 'whatsapp_phone_numbers.setup.connect_waiting_help_title'
            : 'whatsapp_phone_numbers.setup.connect_waiting_title',
        )}
      </AlertTitle>
      <AlertDescription>
        {t(
          showHelp
            ? 'whatsapp_phone_numbers.setup.connect_waiting_help_description'
            : 'whatsapp_phone_numbers.setup.connect_waiting_description',
        )}
      </AlertDescription>
      {/* No filled CTA: the next step is inside Meta's window, and both of ours are recovery paths. */}
      <div className="flex flex-col items-start gap-2.5 pt-3.5 !pl-0">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={onReopen}
            size="sm"
            variant="secondary"
            disabled={continueLoading}
            data-testid="whatsapp-connect-waiting-reopen"
          >
            {t('whatsapp_phone_numbers.setup.connect_waiting_reopen_button')}
          </Button>
          <Button
            onClick={onContinueInTab}
            size="sm"
            variant="secondary"
            disabled={continueLoading}
            aria-busy={continueLoading || undefined}
            data-testid="whatsapp-connect-waiting-continue-in-tab"
          >
            {continueLoading && <Spinner className="size-4" />}
            {t('whatsapp_phone_numbers.setup.connect_recovery_in_tab_button')}
          </Button>
        </div>
        <Button
          onClick={onCancel}
          size="sm"
          variant="link"
          className="h-auto px-0 py-0 font-normal text-info"
          disabled={continueLoading}
          data-testid="whatsapp-connect-waiting-cancel"
        >
          {t('whatsapp_phone_numbers.setup.connect_waiting_cancel_button')}
        </Button>
      </div>
    </Alert>
  );
};
